import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { getStripeEnvironment } from "@/lib/stripe";

type VendorOption = "rent" | "own";

const VendorRegistrationForm = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [option, setOption] = useState<VendorOption | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    initials: "",
  });

  const amountDue = option === "rent" ? 1500 : option === "own" ? 500 : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!option) {
      toast.error("Please select a vendor option.");
      return;
    }
    const fullName = form.full_name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const initials = form.initials.trim();

    if (!fullName || !email || !phone) {
      toast.error("Please fill out all required fields.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (phone.replace(/\D/g, "").length < 7) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    if (!agreed || initials.length < 2) {
      toast.error("Please initial and confirm the vendor item condition agreement.");
      return;
    }

    setLoading(true);
    try {
      const { data: checkoutData, error: checkoutErr } = await supabase.functions.invoke(
        "create-popup-checkout",
        {
          body: {
            kind: option === "rent" ? "vendor_rent" : "vendor_host",
            email,
            fullName,
            phone,
            agreementInitials: initials,
            environment: getStripeEnvironment(),
          },
        },
      );
      if (checkoutErr) throw checkoutErr;
      if (checkoutData?.error) {
        toast.error(checkoutData.error);
        setLoading(false);
        return;
      }
      if (checkoutData?.clientSecret) {
        setClientSecret(checkoutData.clientSecret);
        setSubmitted(true);
      } else {
        throw new Error("No checkout session returned");
      }
    } catch (err) {
      console.error("Vendor checkout error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-secondary/40 bg-secondary/10 p-6 text-center space-y-3">
          <div className="mx-auto w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
            <Check className="w-6 h-6 text-secondary-foreground" />
          </div>
          <h3 className="font-heading text-xl font-bold text-foreground">Registration Received</h3>
          <p className="text-sm text-muted-foreground">
            Complete payment below to confirm your vendor spot. Vendor spaces are limited and
            are confirmed in the order payments are received. A confirmation with event details
            will be emailed to <strong>{form.email}</strong> once payment is complete.
          </p>
        </div>
        {clientSecret && <StripeEmbeddedCheckout clientSecret={clientSecret} />}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Option selector */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-foreground">Choose your vendor option</Label>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            {
              value: "rent" as const,
              title: "Rent a Table",
              price: "$15",
              desc: "Silver Sage provides the table.",
            },
            {
              value: "own" as const,
              title: "Bring Your Own Table",
              price: "$5 host fee",
              desc: "You bring your own table.",
            },
          ].map((opt) => {
            const selected = option === opt.value;
            return (
              <button
                type="button"
                key={opt.value}
                onClick={() => setOption(opt.value)}
                className={`text-left rounded-lg border-2 p-4 transition-all ${
                  selected
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-heading font-bold text-foreground">{opt.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{opt.desc}</p>
                  </div>
                  <span className="font-body font-bold text-primary whitespace-nowrap">
                    {opt.price}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contact fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="vendor_full_name">Full Name *</Label>
          <Input
            id="vendor_full_name"
            required
            maxLength={100}
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vendor_email">Email Address *</Label>
          <Input
            id="vendor_email"
            type="email"
            required
            maxLength={255}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="vendor_phone">Phone Number *</Label>
          <Input
            id="vendor_phone"
            type="tel"
            required
            maxLength={30}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
      </div>

      {/* Sales notice */}
      <div className="rounded-md bg-secondary/10 border border-secondary/30 p-4 text-sm text-foreground">
        Vendors are responsible for pricing their own items and will keep all proceeds earned
        from their sales.
      </div>

      {/* Agreement */}
      <div className="rounded-md border border-border bg-card p-4 space-y-3">
        <p className="text-sm text-foreground leading-relaxed">
          <strong>Item Condition Agreement:</strong> I agree that I will not attempt to sell
          items that are heavily damaged, unusable, or incomplete. Examples include books with
          pages ripped out, games or activities missing necessary pieces, broken materials, or
          items that cannot reasonably be used by another family.
        </p>
        <div className="flex items-start gap-3">
          <Checkbox
            id="vendor_agree"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(v === true)}
          />
          <Label htmlFor="vendor_agree" className="text-sm font-normal cursor-pointer">
            I have read and agree to the item condition agreement above.
          </Label>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vendor_initials">
            Initials acknowledging the vendor item condition agreement *
          </Label>
          <Input
            id="vendor_initials"
            required
            maxLength={10}
            minLength={2}
            placeholder="e.g. JD"
            value={form.initials}
            onChange={(e) => setForm({ ...form, initials: e.target.value.toUpperCase() })}
            className="w-24"
          />
        </div>
      </div>

      {/* Pre-submit notice */}
      <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-900">
        Your vendor spot is confirmed once payment is completed on the next step. Vendor spaces
        are limited and are confirmed in the order payments are received.
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full font-body font-bold text-base h-12"
      >
        {loading
          ? "Loading payment…"
          : option
            ? `Continue to Payment — $${(amountDue / 100).toFixed(0)}`
            : "Select an option to continue"}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        Secure payment powered by Stripe.
      </p>
    </form>
  );
};

export default VendorRegistrationForm;
